<template>
  <v-menu
    v-model="profileMenuOpen"
    open-on-click
    scrim
    class="kp-app-bar-profile-menu"
    :width="smAndUp ? 'auto' : '100%'"
    @update:model-value="onProfileMenuToggle"
  >
    <template #activator="{ props }">
      <div
        v-tooltip:bottom="fullName"
        class="tooltip-wrapper"
        data-kp-anchor="layout.default.profile-button"
        v-bind="props"
      >
        <KpAvatar
          :avatar-url="avatar"
          class="cursor-pointer kp-app-bar__welcome"
          :class="smAndUp ? `ml-4 mr-${layoutOutsideSpacing}` : `mx-2 mr-${layoutOutsideSpacing}`"
          size="2rem"
          v-bind="props"
        />
      </div>
    </template>
    <v-list class="kp-app-bar__welcome-dropdown mt-2 pa-2">
      <v-list-item
        class="pb-1"
        :style="{marginLeft:'1px'}"
      >
        <template #prepend>
          <v-avatar size="32">
            <v-img
              :src="avatar"
              cover
            />
          </v-avatar>
        </template>
        <v-list-item-title>
          {{ fullName }}
          <v-list-item-subtitle>
            {{ accessFriendlyName }}
          </v-list-item-subtitle>
        </v-list-item-title>
      </v-list-item>
      <v-divider class="pb-1" />
      <v-list-item
        class="kp-app-bar__preferences rounded list-item-profile-menu"
        prepend-icon="account_circle"
        :active="false"
        :title="t('user.profile')"
        :to="{'name': 'myProfile' }"
        slim
      />
      <v-list-item
        class="kp-app-bar__change-password rounded list-item-profile-menu"
        :active="false"
        :title="t('menu.secondary.changeMyPassword')"
        :to="{'name': 'myProfile', hash: '#change-password' }"
        prepend-icon="lock_open no-fill"
        slim
      />
      <v-divider class="mt-1" />
      <v-list-subheader>
        {{ t('user.settings') }}
      </v-list-subheader>
      <v-list-item
        prepend-icon="translate no-fill"
        append-icon="chevron_right"
        class="cursor-pointer rounded list-item-profile-menu"
        :title="t('user.language')"
        slim
        value=""
      >
        <v-menu
          open-on-click
          :close-on-content-click="false"
          activator="parent"
          submenu
          :location="smAndUp ? 'end' : 'bottom'"
        >
          <v-list
            class="mr-2"
            :style="smAndUp ? { bottom: '7px' } : {}"
          >
            <v-list-item
              v-for="language in availableLanguages"
              :key="language.value"
              :disabled="locale === language.value || !language.enabled"
              :title="language.name"
              class="list-item-profile-menu mx-2 rounded"
              @click="updateLanguageWrapper(language.value)"
            >
              <template #prepend>
                <v-avatar size="24">
                  <img
                    :src="'data:image/png;base64,' + language.props.avatar"
                    :alt="language.name"
                  >
                </v-avatar>
              </template>
              <template #append>
                <v-progress-circular
                  v-if="updatingLanguage && languageBeingApplied === language.value"
                  indeterminate
                  size="24"
                  width="2"
                />
                <v-icon
                  v-else
                  :icon="locale === language.value ? 'check' : undefined"
                  class="cursor-pointer"
                />
              </template>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-list-item>
      <v-divider class="my-1" />
      <v-list-item
        class="kp-app-bar__logout rounded list-item-profile-menu"
        :title="t('menu.secondary.logout')"
        prepend-icon="mode_off_on no-fill"
        slim
        @click="showLogoutModal = true"
      />
      <v-list-item
        class="text-body-small text-center mt-1"
        density="compact"
        style="min-height: 24px"
      >
        <span class="text-disabled text-label-small">
          {{ t('common.applicationVersion', { version: applicationVersion }) }}
        </span>
      </v-list-item>
    </v-list>
  </v-menu>
  <ConfirmDialog
    v-if="showLogoutModal"
    :title="t('menu.secondary.logout')"
    :show-close="true"
    :show-dialog="true"
    width="558"
    @cancel="showLogoutModal=false"
    @dialog="confirmLogout"
  >
    <template #default>
      <!-- eslint-disable vue/no-v-html -->
      <span v-html="t('alert.logout')" />
    </template>
  </ConfirmDialog>
  <ConfirmDialog
    v-if="showReloadConfirmationDialog"
    :show-close="false"
    :show-dialog="showReloadConfirmationDialog"
    :title="t('user.setLanguageConfirmDialog.title')"
    @cancel="handleLanguageConfirmDialog(false)"
    @dialog="handleLanguageConfirmDialog"
  >
    {{ t('user.setLanguageConfirmDialog.body') }}
  </ConfirmDialog>
</template>
<script setup>
import { useDisplay } from 'vuetify';
import {
  computed, inject, ref,
} from 'vue';
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useI18n } from 'vue-i18n';
import { useStore } from '~/store';
import KpAvatar from '../components/common/keyprod/KpAvatar.vue';
import useLanguage from '~/composables/useLanguage';
import { ME_USER_FIELDS } from '~/graphql/fragments';
import * as types from '~/store/mutationTypes';
import useAuth from '~/composables/useAuth';
import ConfirmDialog from '~/components/common/others/ConfirmDialog.vue';
import useAppLayout from '~/composables/useAppLayout';

const {
  availableLanguages,
  locale,
} = useLanguage();
const { smAndUp } = useDisplay();

const { t } = useI18n();
const store = useStore();

/**
 * Manage the visibility of the notification drawer
 */
const showNotificationsDrawer = inject('showNotificationsDrawer');

/**
 * Close Notifications drawer when profile menu is clicked
 */
const onProfileMenuToggle = () => {
  showNotificationsDrawer.value = false;
};

/**
 * Show/hide logout confirmation modal
 */

const {
  mutate: updateLanguage,
  onDone: onDoneUpdateLanguage,
  loading: updatingLanguage,
} = useMutation(
  gql`
  mutation updateMe(
    $language: UserLanguage
  ) {
    updateMe(
      input: {
        language: $language
      }
    ){
      ...MeUserFields
    }
  }
  ${ME_USER_FIELDS}
`,
);

// Reference to the profile menu model
const profileMenuOpen = ref(false);

onDoneUpdateLanguage((result) => {
  // Update the user in the store with the new language
  store.commit(`auth/${types.FETCH_USER_SUCCESS}`, { user: result?.data?.updateMe });
  // Close the profile menu after language has been updated
  profileMenuOpen.value = false;
});
const languageBeingApplied = ref(null);
// Show/hide the confirmation dialog to reload the application after changing the language
const showReloadConfirmationDialog = ref(false);

/**
 * Update the language
 * @param language
 */
async function updateLanguageWrapper(language) {
  languageBeingApplied.value = language;
  await updateLanguage({ language: language.toUpperCase() });
  // Open the confirmation dialog to reload the application
  showReloadConfirmationDialog.value = true;
}

/**
 * Handle the confirmation dialog after the language change
 * @param confirmed
 */
function handleLanguageConfirmDialog(confirmed) {
  if (confirmed) {
    // Reload the application
    window.location.reload();
  } else {
    // Simply close the dialog
    showReloadConfirmationDialog.value = false;
  }
}

/**
 * Manage the visibility of various dialog modals
 */
const showLogoutModal = ref(false);

const {
  activeUser,
  accessFriendlyName,
  applicationVersion,
  avatar,
  logout,
  logoutFromDirectory,
  signedInThroughDirectory,
  redirectToLogin,
} = useAuth();
/**
 * Get the full name of the user if set otherwise return empty string
 */
const fullName = computed(() => activeUser.value?.fullName ?? '');

/**
 * Logout the user
 */
const confirmLogout = async () => {
  showLogoutModal.value = false;

  // Le seul appelant de la jambe annuaire, et seulement quand CETTE session est venue de
  // là. Le mode du compte ne suffit pas : un administrateur porte les deux voies et entre
  // par l'une ou l'autre selon le jour. Qui vient de taper son mot de passe n'a pas de
  // session d'annuaire à fermer, et l'y envoyer lui ferait traverser un sélecteur de
  // comptes qui ne le concerne pas.
  if (await signedInThroughDirectory()) {
    // Part vers l'annuaire quand le tenant en porte l'URL ; sinon ferme ce qui peut l'être
    // et signale le défaut, sans deviner d'adresse.
    if (await logoutFromDirectory()) {
      return;
    }
  } else {
    await logout();
  }

  await redirectToLogin();
};

const { layoutOutsideSpacing } = useAppLayout();

</script>
